<template>
    <fragment>
        <button v-if="hasChildren" 
                :class="['grid-cell', 'first-column-container', 'app-sticky-column', 'parent', { 'children-visible': childrenVisible, 'selected': isSelected(0) }]" 
                @click="handleCellClick(0)" @blur="handleCellBlur" :aria-expanded="childrenVisible.toString()" 
                :aria-label="getFirstColumnAriaLabel()" 
                :aria-rowindex="ariaRowIndex"
                :aria-colindex="1"
                role="cell" 
                :tabindex="isSelected(0) ? '0' : '-1'">
            <div class="first-column-inner">
                <div class="display-name">
                    {{ item.DisplayName }}
                </div>
            </div>
        </button>
        <div v-else :class="['grid-cell', 'first-column-container', 'app-sticky-column', { 'child': isChild, 'selected': isSelected(0) }]" 
                    @click="handleCellClick(0)" @blur="handleCellBlur" 
                    :aria-label="getFirstColumnAriaLabel()" 
                    :aria-rowindex="ariaRowIndex"
                    :aria-colindex="1"
                    role="cell" 
                    :tabindex="isSelected(0) ? '0' : '-1'">
            <div class="first-column-inner">
                <div class="display-name">
                    {{ item.DisplayName }}
                </div>
                <tags :item="item" />
            </div>
        </div>
        <div :class="['grid-cell', { 'selected': isSelected(1) }]" 
             @click="handleCellClick(1)" 
             @blur="handleCellBlur" 
             :aria-label="`Serving: ${item.ServingDisplay || 'Not specified'}`" 
             :aria-rowindex="ariaRowIndex"
             :aria-colindex="2"
             role="cell" 
             :tabindex="isSelected(1) ? '0' : '-1'">{{ item.ServingDisplay }}
        </div>
        <div :class="['grid-cell', 'allergens-cell', { 'selected': isSelected(2) }]" 
             @click="handleCellClick(2)" 
             @blur="handleCellBlur" 
             :aria-label="getAllergensAriaLabel()" 
             :aria-rowindex="ariaRowIndex"
             :aria-colindex="3"
             role="cell" 
             :tabindex="isSelected(2) ? '0' : '-1'">
            <allergens v-if="!hasChildren" :item="item" variant="small" />
        </div>
        <div v-for="(column, index) in nutritionColumns" 
            :key="column.NutritionId" 
            :class="['grid-cell', { 'selected': isSelected(index + 3) }]" 
            @click="handleCellClick(index + 3)" 
            @blur="handleCellBlur" 
            :aria-label="getNutritionAriaLabel(column)" 
            :aria-rowindex="ariaRowIndex"
            :aria-colindex="index + 4"
            role="cell" 
            :tabindex="isSelected(index + 3) ? '0' : '-1'">
            <fragment v-if="!hasChildren">
                <span class="amount" v-if="item.NutritionAmounts && item.NutritionAmounts[column.NutritionComponentId] && item.NutritionAmounts[column.NutritionComponentId].AmountF != null">
                    <span class="value">{{ item.NutritionAmounts[column.NutritionComponentId].AmountF }}</span>
                    <span class="unit">{{ item.NutritionAmounts[column.NutritionComponentId].Unit }}</span>
                </span>
                <not-set-icon
                    v-else
                    tooltip-text="Not set by District"
                    prefix="Nutrition"
                    size="medium"
                />
            </fragment>
        </div>
        
    </fragment>
</template>

<script>
import { Fragment } from 'vue-frag';
import Allergens from '../../components/allergens.vue';
import NotSetIcon from '../../components/notSetIcon.vue';
import Tags from '../../components/tags.vue';
import menuItemMixin from '../../js/mixins/menuItemMixin';

export default {
    name: 'menu-item-row',
    mixins: [menuItemMixin],
    components: {
        'fragment': Fragment,
        'allergens': Allergens,
        'tags': Tags,
        'not-set-icon': NotSetIcon
    },
    props: {
        item: Object,
        rowData: Object,
        rowIndex: {
            type: Number,
            default: 0
        },
        selectionContext: {
            type: Object,
            default: () => ({
                selectedRow: -1,
                selectedCol: -1,
                hasFocus: false,
                totalRows: 0,
                totalColumns: 0,
                ariaRowOffset: 2
            })
        },
        childrenVisible: Boolean
    },
    data() {
        return {}
    },
    computed: {
        nutritionColumns() {
            return this.$store.state.PublicMenuModule.nutrition;
        },
        isChild() {
            return this.rowData?.isChild || false;
        },
        hasChildren() {
            return this.rowData?.hasChildren || false;
        },
        ariaRowIndex() {
            return this.rowIndex + this.selectionContext.ariaRowOffset;
        }
    },
    methods: {
        isSelected(col) {
            return this.selectionContext.hasFocus && this.selectionContext.selectedRow === this.rowIndex && this.selectionContext.selectedCol === col;
        },
        handleCellClick(col) {
            if (col === 0 && this.hasChildren) {
                this.$emit('toggle-children');
            }
            this.$emit('cell-click', this.rowIndex, col);
        },
        handleCellBlur() {
            this.$emit('cell-blur');
        },
        getNutritionAriaLabel(column) {
            const amount = this.item.NutritionAmounts && this.item.NutritionAmounts[column.NutritionComponentId];
            if (amount && amount.AmountF != null) {
                return `${column.ShortName}: ${amount.AmountF} ${amount.Unit}`;
            }
            return `${column.ShortName}: Not specified`;
        },
        getFirstColumnAriaLabel() {
            let label = `Menu Item: ${this.item.DisplayName}`;
            
            if (this.item.Tags && this.item.Tags.length > 0) {
                label += `. Tags: ${this.item.Tags.join(', ')}`;
            }
            
            if (this.hasChildren) {
                label += `. ${this.childrenVisible ? 'Expanded' : 'Collapsed'} submenu with ${this.item.ChildItems?.length || 0} items`;
            }
            
            return label;
        },
        getAllergensAriaLabel() {
            const allergenLabel = this.getAllAllergensForLabel(this.item);
            return `Allergens: ${allergenLabel}`;
        },
    },
};
</script>

<style scoped>
.grid-cell {
    text-align: right; 
    padding: 8px 12px;
    background-color: white;
    white-space: nowrap;
    border-bottom: 1px solid rgba(225, 225, 226, 1);
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.first-column-container {
    text-align: left;
    display: flex;
    width: 100%;
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: white;
    justify-content: flex-start;
    white-space: normal;
}

.first-column-container.app-sticky-column {
    z-index: 6;
}

.first-column-container::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: rgba(225, 225, 226, 1);
}

.first-column-inner {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    width: 100%;
}

.display-name {
    word-wrap: normal;
    overflow-wrap: normal;
    hyphens: none;
    word-break: normal;
}

.menu-item-tags {
    margin-top: 4px;
}

.display-name-allergens {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.amount {
    display: flex;
    flex-direction: row;
    gap: 2px;
}

.amount .value {
    flex: 1;
    text-align: right;
}

.amount .unit {
    flex: 0;
    color: rgba(138, 140, 144, 1);
}

.parent {
    cursor: pointer;
}

button.grid-cell {
    border: none;
    border-bottom: 1px solid rgba(225, 225, 226, 1);
}

.child .first-column-inner {
    padding-left: 20px;
}

.parent::before {
    font-size: 10px;
    content: '\e916';
    font-family: 'e-icons';
    transform: rotate(-90deg);
    transition: transform 300ms ease;
    margin-right: 10px;   
}

.parent.children-visible::before {
    transform: rotate(0deg);
    transition: transform 300ms ease;
}

.child .first-column-container {
    background-color: white;
}

.grid-cell:last-of-type {
    border-bottom-right-radius: 16px;
}

.first-column-container:last-of-type {
    border-bottom-left-radius: 16px;
}

/* Remove rounded corners for parent rows (rows with children) */
.parent.grid-cell:last-of-type {
    border-bottom-right-radius: 0;
}

.parent.first-column-container:last-of-type {
    border-bottom-left-radius: 0;
}

/* Ensure parent rows can show selection styling */
.parent.selected {
    background-color: rgba(194, 230, 110, 0.1) !important;
    outline: 2px solid #C2E66E !important;
    outline-offset: -2px !important;
}

.allergens-cell {
    justify-content: center;
    text-align: center;
}

.allergens-cell > div {
    width: 100%;
    text-wrap: inherit;
}



.grid-cell:focus,
.grid-cell:focus-visible {
    outline: none !important;
    box-shadow: none !important;
}


.grid-cell.selected {
    background-color: rgba(194, 230, 110, 0.1) !important;
    outline: 2px solid #C2E66E !important;
    outline-offset: -2px !important;
    position: relative !important;
    z-index: 5 !important;
}

</style>